import React from "react";
import { Button } from "@fdoh/health-ui-react";
import { FormControl } from "@fdoh/health-ui-react";
import { SoilProfile, SoilProfileHorizon, SelectOption } from "./AbandonmentForm.types";
import { Control } from "react-hook-form";

// Updated props interface to include EHD lookup data and profile header info
export interface SoilProfileTableProps {
    profile: SoilProfile;
    onEdit: (horizon: SoilProfileHorizon) => void;
    onDelete: (horizonId: number) => void;
    onAdd: () => void;
    onUSDAChange: (value: string) => void;
    usdaSoilSeries?: string;
    // Profile header info
    profileNumber: number;
    profileTitle: string;
    // EHD Lookup Data
    munsellColors: SelectOption[];
    soilTextures: SelectOption[];
    usdaSoilSeriesOptions: SelectOption[];
    // Depth fields
    depthFieldName: string;
    benchmarkFieldName: string;
    isRequired?: boolean;
    // Form control
    control: Control<any>;
}

export const SoilProfileTable: React.FC<SoilProfileTableProps> = ({
    profile,
    onEdit,
    onDelete,
    onAdd,
    onUSDAChange,
    usdaSoilSeries,
    profileNumber,
    profileTitle,
    munsellColors,
    soilTextures,
    usdaSoilSeriesOptions,
    depthFieldName,
    benchmarkFieldName,
    isRequired = false,
    control
}) => {
    
    // Benchmark options for dropdown
    const benchmarkOptions = [
        { id: 0, name: "Select..." },
        { id: 1, name: "Above" },
        { id: 2, name: "Below" }
    ];
    return (
        <div className="soil-profile-table">
            {/* Profile Header */}
            <div className="profile-header">
                <div className="profile-left">
                    <div className="profile-number">
                        <span className="number-badge">{profileNumber}</span>
                        <div className="profile-info">
                            <h4 className="profile-title">{profileTitle}</h4>
                            <span className="horizon-count">{profile.horizons.length} Horizon{profile.horizons.length !== 1 ? 's' : ''}</span>
                        </div>
                    </div>
                </div>
                <div className="profile-right">
                    <span className={`status-indicator ${profile.horizons.length > 0 ? 'complete' : 'incomplete'}`}>
                        {profile.horizons.length > 0 ? 'Complete' : 'Incomplete'}
                    </span>
                </div>
            </div>

            {/* Content Area */}
            <div className="table-content">
                {/* USDA Soil Series Selection - Compact */}
                <div className="usda-compact-section">
                    <div className="usda-field-group">
                        <label className="usda-label">
                            USDA Soil Series
                            {profile.horizons.length > 0 && (
                                <span className="required-indicator">*</span>
                            )}
                        </label>
                        <select
                            value={usdaSoilSeries || ""}
                            onChange={(e) => onUSDAChange(e.target.value)}
                            className={`usda-select ${profile.horizons.length > 0 && (!usdaSoilSeries || usdaSoilSeries.trim() === '') ? 'usda-error' : ''}`}
                            title="Select the appropriate USDA soil series classification"
                        >
                            <option value="">Choose soil series...</option>
                            {usdaSoilSeriesOptions.map((option) => (
                                <option key={option.id} value={option.name}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Profile Depth Fields */}
                <div className="depth-fields-section">
                    <div className="depth-fields-grid">
                        <div className="depth-field-group">
                            <FormControl.Text
                                control={control}
                                name={depthFieldName}
                                label="Profile Depth (inches)"
                                type="number"
                                required={isRequired}
                            />
                        </div>
                        <div className="benchmark-field-group">
                            <FormControl.Select
                                control={control}
                                name={benchmarkFieldName}
                                label="Above or below benchmark?"
                                options={benchmarkOptions}
                                required={isRequired}
                            />
                        </div>
                    </div>
                </div>

                {/* Horizons Display */}
                {profile.horizons.length > 0 ? (
                    <div className="horizons-section">
                        <div className="section-header">
                            <div className="header-icon">
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                    <rect x="2" y="2" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M2 9H16M9 2V16" stroke="currentColor" strokeWidth="2"/>
                                </svg>
                            </div>
                            <h5 className="section-title">Soil Horizons</h5>
                            <Button
                                onClick={onAdd}
                                className="add-horizon-btn"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                                Add Horizon
                            </Button>
                        </div>
                        
                        <div className="horizons-table-container">
                            <table className="horizons-table">
                                <thead>
                                    <tr>
                                        <th className="th-horizon">Horizon</th>
                                        <th className="th-depth">Depth (cm)</th>
                                        <th className="th-color">Munsell Color</th>
                                        <th className="th-texture">Soil Texture</th>
                                        <th className="th-description">Description</th>
                                        <th className="th-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {profile.horizons.map((horizon, index) => (
                                        <tr key={horizon.id} className="horizon-row">
                                            <td className="td-horizon">
                                                <span className="horizon-badge">
                                                    {horizon.horizonNumber}
                                                </span>
                                            </td>
                                            <td className="td-depth">
                                                <span className="depth-range">
                                                    {horizon.depthFrom} - {horizon.depthTo}
                                                </span>
                                            </td>
                                            <td className="td-color">
                                                <span className="color-chip">
                                                    {horizon.munsellColor || 'Not specified'}
                                                </span>
                                            </td>
                                            <td className="td-texture">
                                                <span className="texture-chip">
                                                    {horizon.soilTexture || 'Not specified'}
                                                </span>
                                            </td>
                                            <td className="td-description">
                                                <span className="description-text">
                                                    {horizon.colorDescription || '—'}
                                                </span>
                                            </td>
                                            <td className="td-actions">
                                                <div className="action-buttons">
                                                    <button
                                                        onClick={() => onEdit(horizon)}
                                                        className="action-btn edit"
                                                        title="Edit Horizon"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M9.5 1.5H4.5C4.22 1.5 4 1.72 4 2V12C4 12.28 4.22 12.5 4.5 12.5H9.5C9.78 12.5 10 12.28 10 12V2C10 1.72 9.78 1.5 9.5 1.5Z" stroke="currentColor" strokeWidth="1.5"/>
                                                            <path d="M7 4H5.5M7 6H5.5M7 8H5.5M8.5 4H8.5.01M8.5 6H8.5.01M8.5 8H8.5.01" stroke="currentColor" strokeWidth="1.5"/>
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => onDelete(horizon.id || horizon.horizonNumber)}
                                                        className="action-btn delete"
                                                        title="Delete Horizon"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M2 3.5H12M5 3.5V2.5C5 2.22 5.22 2 5.5 2H8.5C8.78 2 9 2.22 9 2.5V3.5M11 3.5V11.5C11 11.78 10.78 12 10.5 12H3.5C3.22 12 3 11.78 3 11.5V3.5H11Z" stroke="currentColor" strokeWidth="1.5"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    /* Empty State */
                    <div className="empty-state">
                        <div className="empty-icon">
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <rect x="8" y="8" width="32" height="32" rx="4" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4"/>
                                <path d="M24 20V28M20 24H28" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                        </div>
                        <h6 className="empty-title">No Horizons Added Yet</h6>
                        <p className="empty-description">
                            Start building your soil profile by adding the first horizon layer
                        </p>
                        <Button
                            onClick={onAdd}
                            className="add-first-horizon-btn"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Add First Horizon
                        </Button>
                    </div>
                )}

                {/* Profile Summary Cards */}
                {profile.horizons.length > 0 && (
                    <div className="summary-section">
                        <div className="summary-grid">
                            <div className="summary-card">
                                <div className="card-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <div className="card-content">
                                    <h6 className="card-title">Total Horizons</h6>
                                    <span className="card-value">{profile.horizons.length}</span>
                                </div>
                            </div>
                            
                            <div className="summary-card">
                                <div className="card-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <div className="card-content">
                                    <h6 className="card-title">USDA Series</h6>
                                    <span className="card-value">
                                        {usdaSoilSeries || 'Not selected'}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="summary-card">
                                <div className="card-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                                        <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                </div>
                                <div className="card-content">
                                    <h6 className="card-title">Profile Status</h6>
                                    <span className={`status-badge ${profile.horizons.length > 0 ? 'active' : 'empty'}`}>
                                        {profile.horizons.length > 0 ? 'Active' : 'Empty'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modern CSS Styles */}
            <style>{`
                .soil-profile-table {
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e8eaed;
                    overflow: hidden;
                    transition: all 0.2s ease;
                }
                
                .soil-profile-table:hover {
                    border-color: #667eea;
                    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.1);
                }
                
                /* Profile Header */
                .profile-header {
                    background: #fafbfc;
                    padding: 20px 24px;
                    border-bottom: 1px solid #e8eaed;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    transition: all 0.2s ease;
                }
                
                .profile-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                .profile-right {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                .profile-number {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                .profile-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                
                .number-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 16px;
                    flex-shrink: 0;
                }
                
                .profile-title {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 600;
                    color: #1a1a1a;
                }
                
                .horizon-count {
                    font-size: 13px;
                    color: #666;
                    font-weight: 500;
                }
                
                .status-indicator {
                    padding: 6px 16px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .status-indicator.complete {
                    background: #d1fae5;
                    color: #065f46;
                }
                
                .status-indicator.incomplete {
                    background: #fef3c7;
                    color: #92400e;
                }
                
                /* Content */
                .table-content {
                    padding: 24px;
                }
                
                /* USDA Compact Section */
                .usda-compact-section {
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #e8eaed;
                }

                /* Depth Fields Section */
                .depth-fields-section {
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 1px solid #e8eaed;
                }

                .depth-fields-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                    align-items: end;
                }

                .depth-field-group,
                .benchmark-field-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                @media (max-width: 768px) {
                    .depth-fields-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                }
                
                .usda-field-group {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                
                .usda-label {
    font-weight: 600;
    color: #1a1a1a;
    font-size: 14px;
    white-space: nowrap;
    min-width: 120px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.required-indicator {
    color: #dc2626;
    font-weight: 700;
    font-size: 16px;
}

.usda-help {
    margin: 4px 0 0 0;
    font-size: 12px;
    color: #666;
    line-height: 1.3;
}
                
                .usda-select {
                    flex: 1;
                    padding: 10px 14px;
                    border: 1px solid #cbd5e1;
                    border-radius: 6px;
                    font-size: 14px;
                    background: white;
                    color: #1a1a1a;
                    transition: border-color 0.2s ease;
                }
                
                .usda-select:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
}

.usda-select.usda-error {
    border-color: #dc2626;
    background-color: #fef2f2;
}

.usda-select.usda-error:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.1);
}
                
                /* Horizons Section */
                .horizons-section {
                    margin-bottom: 24px;
                }
                
                .section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid #e8eaed;
                }
                
                .section-header .header-icon {
                    width: 32px;
                    height: 32px;
                    background: #667eea;
                    color: white;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .section-title {
                    margin: 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #1a1a1a;
                    flex: 1;
                }
                
                .soil-profile-table .add-horizon-btn {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
                    color: white !important;
                    border: none !important;
                    padding: 10px 16px !important;
                    border-radius: 8px !important;
                    font-weight: 600 !important;
                    font-size: 13px !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 6px !important;
                    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
                    white-space: nowrap !important;
                }
                
                .soil-profile-table .add-horizon-btn:hover {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4) !important;
                }
                
                /* Horizons Table */
                .horizons-table-container {
                    overflow-x: auto;
                    border-radius: 12px;
                    border: 1px solid #e8eaed;
                    background: white;
                }
                
                .horizons-table {
                    width: 100%;
                    border-collapse: collapse;
                    background: white;
                }
                
                .horizons-table th {
                    background: #f8fafc;
                    padding: 16px 12px;
                    text-align: left;
                    font-weight: 600;
                    font-size: 13px;
                    color: #475569;
                    border-bottom: 2px solid #e2e8f0;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .horizons-table td {
                    padding: 16px 12px;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: middle;
                }
                
                .horizons-table tr:hover {
                    background: #f8fafc;
                }
                
                .horizons-table tr:last-child td {
                    border-bottom: none;
                }
                
                /* Table Column Specific Styles */
                .th-horizon, .td-horizon {
                    width: 80px;
                    text-align: center;
                }
                
                .th-depth, .td-depth {
                    width: 120px;
                }
                
                .th-color, .td-color {
                    width: 140px;
                }
                
                .th-texture, .td-texture {
                    width: 140px;
                }
                
                .th-description, .td-description {
                    min-width: 200px;
                }
                
                .th-actions, .td-actions {
                    width: 100px;
                    text-align: center;
                }
                
                /* Horizon Badge */
                .horizon-badge {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                    font-size: 14px;
                }
                
                /* Depth Range */
                .depth-range {
                    color: #1a1a1a;
                    font-weight: 600;
                    font-size: 14px;
                }
                
                /* Color and Texture Chips */
                .color-chip, .texture-chip {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    font-weight: 600;
                    display: inline-block;
                    white-space: nowrap;
                }
                
                .color-chip {
                    background: #fef3c7;
                    color: #92400e;
                }
                
                .texture-chip {
                    background: #d1fae5;
                    color: #065f46;
                }
                
                /* Description Text */
                .description-text {
                    color: #64748b;
                    font-size: 13px;
                    line-height: 1.4;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                /* Action Buttons */
                .action-buttons {
                    display: flex;
                    gap: 6px;
                    justify-content: center;
                }
                
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border: none;
                    border-radius: 6px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    color: #666;
                }
                
                .action-btn.edit {
                    background: #f0f4ff;
                    color: #667eea;
                }
                
                .action-btn.edit:hover {
                    background: #e0e7ff;
                    transform: scale(1.05);
                }
                
                .action-btn.delete {
                    background: #fef2f2;
                    color: #dc2626;
                }
                
                .action-btn.delete:hover {
                    background: #fee2e2;
                    transform: scale(1.05);
                }
                
                .action-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                
                /* Empty State */
                .empty-state {
                    text-align: center;
                    padding: 40px 20px;
                    color: #666;
                }
                
                .empty-icon {
                    width: 64px;
                    height: 64px;
                    background: #f8f9fa;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                    color: #adb5bd;
                }
                
                .empty-title {
                    margin: 0 0 8px 0;
                    color: #1a1a1a;
                    font-weight: 600;
                    font-size: 16px;
                }
                
                .empty-description {
                    margin: 0 0 20px 0;
                    font-size: 14px;
                    line-height: 1.5;
                }
                
                .soil-profile-table .add-first-horizon-btn {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
                    color: white !important;
                    border: none !important;
                    padding: 12px 24px !important;
                    border-radius: 10px !important;
                    font-weight: 600 !important;
                    font-size: 14px !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    display: inline-flex !important;
                    align-items: center !important;
                    gap: 8px !important;
                    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3) !important;
                }
                
                .soil-profile-table .add-first-horizon-btn:hover {
                    transform: translateY(-1px) !important;
                    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4) !important;
                }
                
                /* Summary Section */
                .summary-section {
                    margin-top: 24px;
                    padding-top: 20px;
                    border-top: 1px solid #e8eaed;
                }
                
                .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 16px;
                }
                
                .summary-card {
                    background: #fafbfc;
                    border-radius: 12px;
                    padding: 20px;
                    border: 1px solid #e8eaed;
                    text-align: center;
                    transition: all 0.2s ease;
                }
                
                .summary-card:hover {
                    border-color: #667eea;
                    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.1);
                }
                
                .card-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 12px;
                }
                
                .card-title {
                    margin: 0 0 8px 0;
                    color: #666;
                    font-weight: 600;
                    font-size: 12px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .card-value {
                    color: #1a1a1a;
                    font-weight: 700;
                    font-size: 18px;
                }
                
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 16px;
                    font-size: 11px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                
                .status-badge.active {
                    background: #d1fae5;
                    color: #065f46;
                }
                
                .status-badge.empty {
                    background: #fef3c7;
                    color: #92400e;
                }
                
                /* Responsive Design */
                @media (max-width: 768px) {
                    .table-header {
                        padding: 20px;
                        flex-direction: column;
                        gap: 16px;
                        align-items: stretch;
                    }
                    
                    .header-left {
                        justify-content: center;
                    }
                    
                    .soil-profile-table .add-horizon-btn {
                        width: 100% !important;
                        justify-content: center !important;
                    }
                    
                    .profile-header {
                        padding: 16px 20px;
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    
                    .profile-left, .profile-right {
                        width: 100%;
                        justify-content: space-between;
                    }
                    
                    .table-content {
                        padding: 20px;
                    }
                    
                    .usda-field-group {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .usda-label {
                        min-width: auto;
                    }
                    
                    .usda-select {
                        width: 100%;
                    }
                    
                    .horizons-table-container {
                        overflow-x: auto;
                        border-radius: 8px;
                    }
                    
                    .horizons-table {
                        min-width: 600px;
                    }
                    
                    .horizons-table th,
                    .horizons-table td {
                        padding: 12px 8px;
                        font-size: 12px;
                    }
                    
                    .th-horizon, .td-horizon {
                        width: 60px;
                    }
                    
                    .th-depth, .td-depth {
                        width: 100px;
                    }
                    
                    .th-color, .td-color {
                        width: 120px;
                    }
                    
                    .th-texture, .td-texture {
                        width: 120px;
                    }
                    
                    .th-description, .td-description {
                        min-width: 150px;
                    }
                    
                    .th-actions, .td-actions {
                        width: 80px;
                    }
                    
                    .summary-grid {
                        grid-template-columns: 1fr;
                    }
                }
                
                @media (max-width: 480px) {
                    .table-header {
                        padding: 16px;
                    }
                    
                    .profile-icon {
                        width: 36px;
                        height: 36px;
                    }
                    
                    .profile-title {
                        font-size: 16px;
                    }
                    
                    .table-content {
                        padding: 16px;
                    }
                    
                    .horizons-table-container {
                        border-radius: 6px;
                    }
                    
                    .horizons-table {
                        min-width: 500px;
                    }
                    
                    .horizons-table th,
                    .horizons-table td {
                        padding: 8px 6px;
                        font-size: 11px;
                    }
                    
                    .horizon-badge {
                        width: 28px;
                        height: 28px;
                        font-size: 12px;
                    }
                    
                    .action-btn {
                        width: 28px;
                        height: 28px;
                    }
                }
            `}</style>
        </div>
    );
};

export default SoilProfileTable;
